import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";

/** Kinds of logical connection an Extended PoP delivers (CTX-797). */
export type ConnectionType =
  | "internet"
  | "datacenter-interconnect"
  | "cloud-connectivity"
  | "fwint";

/** One service the Extended PoP delivers, as `/:id/connections` serves it. */
export interface ExtenderPopConnection {
  type: ConnectionType;
  id: number;
  /** Service ID (13-, 30-, 40-, 61-…). */
  serviceId: string;
  name: string | null;
  /** The customer port it rides on. */
  port: { id: number; serviceId: string };
  /** The CPE behind that port. */
  cpeId: number | null;
  status: string;
  statusMessage: string | null;
}

const TYPE_LABEL: Record<ConnectionType, string> = {
  internet: "Internet",
  "datacenter-interconnect": "Datacenter Interconnect",
  "cloud-connectivity": "Cloud Connectivity",
  fwint: "Firewall Interface",
};

export const connectionTypeLabel = (type: string): string =>
  TYPE_LABEL[type as ConnectionType] ?? type;

/**
 * Where a connection's ID leads: its own detail. A Firewall interface has
 * none — it goes to FWaaS, as it does from the Port connections table.
 */
export const connectionDetailPath = (
  connection: Pick<ExtenderPopConnection, "type" | "id">
): string | null => {
  switch (connection.type) {
    case "internet":
      return buildDetailPath("internet", { connectionId: connection.id });
    case "datacenter-interconnect":
      return buildDetailPath("datacenter", { datacenterId: connection.id });
    case "cloud-connectivity":
      return buildDetailPath("cloudConnectivity", {
        connectionId: connection.id,
      });
    case "fwint":
      return "/kontrol/fwaas";
    default:
      return null;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const shapeConnections = (raw: any): ExtenderPopConnection[] =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (Array.isArray(raw?.connections) ? raw.connections : []).map((c: any) => ({
    type: c.type,
    id: c.id,
    serviceId: c.serviceId ?? "-",
    name: c.name ?? null,
    port: { id: c.port?.id, serviceId: c.port?.serviceId ?? "-" },
    cpeId: c.cpeId ?? null,
    status: c.status ?? "unknown",
    statusMessage: c.statusMessage ?? null,
  }));
